import { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useContacts } from "../reducers/contactSlice"
import { Modal, ReactNodeDialog } from "../common/utils/dialogUtlities"
import Button from "../common/components/Button"
import { DefaultButton } from "../common/components/DefaultButton"
import { Col, Row } from "../common/components/Grid"
import Icon from "../common/components/Icon"
import RichTextEditor from "../common/components/RichTextEditor"
import TextInput from "../common/components/TextInput"
import PropTypes from 'prop-types';
import Checkbox from "../common/components/Checkbox"
import DataTable from "../common/components/DataTable"
import ContactListSelector from "./ContactListSelector"
import { deepClone } from "../common/utils/objectUtilities";

const EmailDialog = ({ message: inputMessage, readonly = false, showModal = false, onChange, onClose, onSend }) => {
    const { t } = useTranslation();
    const contacts = useContacts();
    const [showContactListSelector, setShowContactListSelector] = useState(false);
    const [selectedContacts, setSelectedContacts] = useState();
    const [message, setMessage] = useState(deepClone(inputMessage))
    const [sending, setSending] = useState(false);

    useEffect(() => setMessage(deepClone(inputMessage)), [inputMessage]);

    const handleContactToggle = useCallback(() => {
        setSelectedContacts(selectedContacts => {
            const allSelected = contacts.every(c => selectedContacts[c.id.toString()]);
            return contacts.reduce((selection, contact) => { selection[contact.id.toString()] = !allSelected; return selection; }, { field: selectedContacts.field })
        });
    }, [contacts]);
    const handleContactSelect = useCallback(id => {
        setSelectedContacts(selectedContacts => ({ ...selectedContacts, [id.toString()]: !selectedContacts[id.toString()] }))
    }, [])
    const contactTableDef = useMemo(() => [
        { type: 'dynamic', key: 'select', uuid: 'select', title: <Checkbox hideLabel={true} checked={contacts.every(c => selectedContacts?.[c.id.toString()])} onChange={handleContactToggle}></Checkbox>, def: (value, disabled, hideLabel, onChange, onUpdate) => <Checkbox checked={selectedContacts?.[value.id.toString()]} disabled={disabled} hideLabel={hideLabel} onChange={() => handleContactSelect(value.id)}></Checkbox> },
        { type: 'text', title: t('name'), field: 'name', readonly: true },
        { type: 'email', title: t('email'), field: 'email', readonly: true }
    ], [t, contacts, selectedContacts, handleContactToggle, handleContactSelect])

    const handleChange = useCallback((field, value) => {
        setMessage(message => ({ ...message, [field]: value }));
        onChange?.({ ...message, [field]: value });
    }, [onChange, message]);
    
    const addContacts = useCallback(field => {
        const selected = { field };
        message[field]?.split(';')?.forEach(email => {
            const contact = contacts.find(c => c.email && c.email.toLowerCase().trim() === email.toLowerCase().trim());
            if (contact) {
                selected[contact.id] = true;
            }
        });
        setSelectedContacts(selected);
        setShowContactListSelector(false);
    }, [contacts, message]);

    const handleSaveContacts = useCallback(() => {
        setMessage(message => {
            message[selectedContacts.field] = Object.keys(selectedContacts).filter(f => f !== 'field' && selectedContacts[f]).map(f => contacts.find(c => c.id.toString() === f).email).join('; ');
            setSelectedContacts();
            return message;
        })
    }, [selectedContacts, contacts]);

    const selectListContacts = useCallback((listContacts) => {
        setSelectedContacts(selectedContacts => {
            listContacts.forEach(c => selectedContacts[c.id.toString()] = true);
            return selectedContacts;
        })
        setShowContactListSelector(false);
    }, []);

    return <>
        <ContactListSelector kind={selectedContacts?.field} show={showContactListSelector} onClose={() => setShowContactListSelector(false)} addContacts={selectListContacts}></ContactListSelector>
        <ReactNodeDialog showModal={selectedContacts && !showContactListSelector} head size="lg" title={t('selectContacts')} onSave={() => handleSaveContacts()} onClose={() => setSelectedContacts()}>
            <Row>
                <Col></Col>
                <Col xs="auto">
                    <Button variant="primary" onClick={() => setShowContactListSelector(true)}><Icon variant="solid" kind="users"></Icon> {t('contactList', { count: 100})}</Button>
                </Col>
            </Row>
            <Row>
                <Col>
                    {selectedContacts ? <DataTable columns={contactTableDef} value={contacts} disableAdd={true} disableRemove={true} defaultSort={['name']} emptyMessage={t('noContactsFound')} style={{ maxHeight: 'calc(100vh - 260px)'}}></DataTable> : null}
                </Col>
            </Row>
        </ReactNodeDialog>
        <Modal show={showModal && !selectedContacts && !showContactListSelector} size="xlg">
            <Modal.Header>
                <Modal.Title>{t('composeEmail')}</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{maxHeight: 'calc(100vh - 180px)', overflowY: 'auto'}}>
                {showModal ? <>
                <Row>
                    <Col xs={2}>
                        {t('toRecipients')}
                    </Col>
                    <Col xs={8}>
                        <TextInput value={message.to} hideLabel={true} placeholder={t('toRecipients')} onChange={value => handleChange('to', value)}></TextInput>
                    </Col>
                    <Col xs={2}>
                        <Button className="mt-2" variant="default" onClick={() => addContacts('to')}><Icon variant="regular" kind="address-book"></Icon> {t('contact', { count: 100 })}</Button>
                    </Col>
                </Row>
                <Row>
                    <Col xs={2}>
                        {t('ccRecipients')}
                    </Col>
                    <Col xs={8}>
                        <TextInput value={message.cc} hideLabel={true} placeholder={t('ccRecipients')} onChange={value => handleChange('cc', value)}></TextInput>
                    </Col>
                    <Col xs={2}>
                        <Button className="mt-2" variant="default" onClick={() => addContacts('cc')}><Icon variant="regular" kind="address-book"></Icon> {t('contact', { count: 100 })}</Button>
                    </Col>
                </Row>
                <Row>
                    <Col xs={2}>
                        {t('bccRecipients')}
                    </Col>
                    <Col xs={8}>
                        <TextInput value={message.bcc} hideLabel={true} placeholder={t('bccRecipients')} onChange={value => handleChange('bcc', value)}></TextInput>
                    </Col>
                    <Col xs={2}>
                        <Button className="mt-2" variant="default" onClick={() => addContacts('bcc')}><Icon variant="regular" kind="address-book"></Icon> {t('contact', { count: 100 })}</Button>
                    </Col>
                </Row>
                <Row>
                    <Col xs={2}>
                        {t('subject')}
                    </Col>
                    <Col xs={8}>
                        <TextInput value={message.subject} hideLabel={true} placeholder={t('subject')} maxLength={256} onChange={value => handleChange('subject', value)}></TextInput>
                    </Col>
                    <Col xs={2}>

                    </Col>
                </Row>
                <Row>
                    <Col>
                        <RichTextEditor value={message.message} hideLabel={true} placeholder={t('message')} readonly={readonly} onChange={value => handleChange('message', value)}></RichTextEditor>
                    </Col>
                </Row>
                </> : null}
            </Modal.Body>
            <Modal.Footer>
                <Row className="w-100">
                    <Col></Col>
                    <Col xs="auto">
                        { sending ? <Button variant="success" disabled={true}><Icon intent="send"></Icon> {t('sending')}</Button> : <DefaultButton className="mx-2" kind="send" disabled={(!message?.to && !message?.cc && !message?.bcc) || !message?.message} onClick={() => { setSending(true); onSend(message)?.then?.(() => setSending(false)); } }></DefaultButton> }
                        <DefaultButton kind="close" onClick={() => onClose()}></DefaultButton>
                    </Col>
                </Row>
            </Modal.Footer>
        </Modal>
    </>;
}

EmailDialog.propTypes = {
    message: PropTypes.object,
    disabled: PropTypes.bool,
    showModal: PropTypes.bool,
    onChange: PropTypes.func,
    onClose: PropTypes.func,
    onSend: PropTypes.func
}

export default EmailDialog